import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { endpoints } from "@/lib/api";
import ProBadge from "@/components/ProBadge";
import { Sparkles } from "lucide-react";

/**
 * Renders the Pro-only "Your Compass Is Evolving" cross-pattern reading.
 *
 * Backend contract (unchanged):
 *   - `{ has_insights: false, reason: "compass_not_taken", message }`
 *   - `{ has_insights: true, archetype, secondary, body, signal_counts }`
 *
 * Presentation intent: this is a personal reflection, not an analytics
 * dashboard. The primary insight lands first at display size; supporting
 * patterns line up beneath it in a quiet, scannable rhythm with copper
 * hairline dividers instead of card walls.
 */
export default function CompassEvolvingInsights() {
  const [state, setState] = useState({ loading: true, data: null, error: null });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await endpoints.compassEvolvingInsights();
        if (!cancelled) setState({ loading: false, data, error: null });
      } catch (e) {
        if (!cancelled) setState({ loading: false, data: null, error: e });
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const eyebrow = (
    <div
      className="flex items-center gap-2 text-[10px] uppercase tracking-[0.28em]"
      style={{ color: "#C56B3F" }}
      data-testid="compass-insights-eyebrow"
    >
      <Sparkles size={11} />
      <span>Your Compass Is Evolving</span>
      <ProBadge />
    </div>
  );

  const container = (children, extraProps = {}) => (
    <section
      className="py-8 md:py-10"
      data-testid={extraProps.testid || "compass-insights"}
    >
      {children}
    </section>
  );

  if (state.loading) {
    return container(
      <>
        {eyebrow}
        <p className="mt-4 text-sm text-[hsl(var(--muted-foreground))]">Reading your patterns…</p>
      </>,
      { testid: "compass-insights-loading" }
    );
  }

  if (state.error || !state.data) {
    return container(
      <>
        {eyebrow}
        <p className="mt-4 text-sm text-[hsl(var(--muted-foreground))]">
          We couldn&rsquo;t load your Compass reading right now.
        </p>
      </>,
      { testid: "compass-insights-error" }
    );
  }

  const { has_insights, message, body, archetype, secondary } = state.data;

  if (!has_insights) {
    return (
      <section className="py-10 md:py-12" data-testid="compass-insights-empty">
        {eyebrow}

        {/* Same warm-taupe italic frame as the fully-populated reading, so
            the empty state hints at the tone of what's coming rather than
            reading as an interruption. */}
        <p
          className="mt-4 font-display italic text-base md:text-lg leading-relaxed max-w-2xl"
          style={{ color: "#746C63" }}
          data-testid="compass-insights-empty-intro"
        >
          This is where your reading will live once your Compass has something to reflect.
        </p>

        {/* The backend's guiding sentence — kept verbatim — becomes the
            primary voice, matching the display treatment of the primary
            insight in the populated state. */}
        <p
          className="mt-6 font-display italic text-2xl md:text-3xl leading-snug max-w-2xl"
          style={{ color: "#292725" }}
          data-testid="compass-insights-empty-message"
        >
          {message}
        </p>

        {/* Quiet copper hairline + call-to-action, using the same visual
            language as the "Evolving patterns" separator so the two states
            share a rhythm. */}
        <div
          className="mt-8 mb-5 w-16 h-px"
          style={{ background: "#C56B3F", opacity: 0.55 }}
          aria-hidden
        />
        <Link
          to="/becoming?section=discover"
          data-testid="compass-insights-take-compass"
          className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.24em] tab-link"
          style={{ color: "#C56B3F" }}
        >
          Take the Compass
          <span aria-hidden>→</span>
        </Link>
      </section>
    );
  }

  return <InsightBody body={body} archetype={archetype} secondary={secondary} eyebrow={eyebrow} />;
}


/**
 * Splits the backend `body` into a primary insight paragraph and the
 * additional patterns that follow, presenting each with quiet visual
 * hierarchy. The backend text is preserved verbatim — we only shape
 * how it *breathes* on the page.
 */
function InsightBody({ body, archetype, secondary, eyebrow }) {
  const { intro, primary, primaryLabel, patterns, coda } = useMemo(() => {
    // Drop the leading "# Your Compass Is Evolving" heading (redundant with
    // our own eyebrow) and any leading blank space.
    let text = String(body || "").replace(/^\s*#[^\n]*\n+/, "").trim();

    // Any trailing italic aphorism (`*…*` on its own line) is our "coda" —
    // rendered at the bottom in a quiet color.
    let codaText = null;
    const codaMatch = text.match(/\n+\*([^*]+)\*\s*$/);
    if (codaMatch) {
      codaText = codaMatch[1].trim();
      text = text.slice(0, codaMatch.index).trim();
    }

    // Split into (label, body) sections keyed on `**Label**` bold headers.
    // If the backend ever changes format, we fall back to naive paragraph
    // splitting so we never crash.
    const sections = [];
    const rx = /\*\*([^*]+)\*\*[ \t]*\n+([\s\S]*?)(?=\n+\*\*|$)/g;
    let m;
    while ((m = rx.exec(text)) !== null) {
      sections.push({ label: m[1].trim(), body: m[2].trim() });
    }
    if (sections.length === 0) {
      // Fallback: treat blank-line-separated paragraphs as untitled sections.
      text.split(/\n\s*\n/).map((p) => p.trim()).filter(Boolean).forEach((body) =>
        sections.push({ label: null, body })
      );
    }

    const first = sections[0] || { label: null, body: "" };
    return {
      intro: null,
      primary: first.body,
      primaryLabel: first.label,
      patterns: sections.slice(1),
      coda: codaText,
    };
  }, [body]);

  return (
    <section
      className="py-10 md:py-12"
      data-testid="compass-insights"
    >
      {eyebrow}

      {/* Introductory perspective — sets the frame without shouting. */}
      <p
        className="mt-4 font-display italic text-base md:text-lg leading-relaxed max-w-2xl"
        style={{ color: "#746C63" }}
        data-testid="compass-insights-intro"
      >
        This is what you&rsquo;re beginning to notice about yourself over time.
      </p>

      {/* Archetype tag — small, neutral. */}
      <div
        className="mt-6 text-[10px] uppercase tracking-[0.28em]"
        style={{ color: "#746C63" }}
        data-testid="compass-insights-archetype"
      >
        {archetype}{secondary ? <>  ·  with {secondary}</> : ""}
      </div>

      {/* Primary insight — the most meaningful cross-pattern. */}
      {primaryLabel && (
        <div
          className="mt-6 text-[11px] uppercase tracking-[0.24em]"
          style={{ color: "#C56B3F" }}
          data-testid="compass-insights-primary-label"
        >
          {primaryLabel}
        </div>
      )}
      <p
        className={`${primaryLabel ? "mt-2" : "mt-4"} font-display italic text-2xl md:text-3xl leading-snug max-w-2xl`}
        style={{ color: "#292725" }}
        data-testid="compass-insights-primary"
      >
        {primary}
      </p>

      {patterns.length > 0 && (
        <>
          {/* Quiet copper hairline separates the insight from the patterns. */}
          <div
            className="mt-10 mb-6 w-16 h-px"
            style={{ background: "#C56B3F", opacity: 0.55 }}
            aria-hidden
          />

          <div
            className="text-[10px] uppercase tracking-[0.28em]"
            style={{ color: "#746C63" }}
            data-testid="compass-insights-patterns-label"
          >
            Evolving patterns
          </div>

          <div className="mt-5 space-y-6 max-w-2xl" data-testid="compass-insights-patterns">
            {patterns.map((s, i) => (
              <div key={i} data-testid={`compass-insights-pattern-${i}`}>
                {s.label && (
                  <div
                    className="text-[11px] uppercase tracking-[0.22em] mb-1.5"
                    style={{ color: "#C56B3F" }}
                  >
                    {s.label}
                  </div>
                )}
                <p
                  className="text-[15px] leading-relaxed"
                  style={{ color: "rgba(41,39,37,0.9)" }}
                >
                  {s.body}
                </p>
              </div>
            ))}
          </div>
        </>
      )}

      {coda && (
        <p
          className="mt-10 font-display italic text-base md:text-lg leading-relaxed max-w-2xl"
          style={{ color: "#746C63" }}
          data-testid="compass-insights-coda"
        >
          {coda}
        </p>
      )}
    </section>
  );
}
